// Text messaging for this line's number.
//
// WHERE IT WAS. In the same hidden "account-wide" drawer, as a table of every number the
// customer owns with an Enable button per row — so turning texting on for one number meant
// finding it in a list on a different page from the line it belongs to.
//
// MESSAGING IS PER NUMBER at the carrier: each DID is its own mailbox, with its own inbound
// and delivery URLs pointed at Verso. A line is a number, so this is the line's question and
// this is where it is asked.
//
// THE SAME THREE DECISIONS AS THE VOICEMAIL TAB, in the same order and the same shapes. Kevin,
// 2026-08-19: "text messaging should have the same email options and should have the same look and
// feel as voicemails."
//
//   whether texts arrive at all — the carrier feature on this number (the switch in the header);
//   whether anything is emailed — `dids.sms_email_enabled` (0062), this number's own answer,
//     falling back to the account's until somebody sets it;
//   who receives it — each person's `sms_notify`, a slider per row.
//
// WHAT WAS HERE INSTEAD: a "For the whole account" card stating the tier's messaging feature, one
// legacy email address, a link to change it on the subscriber, and a sentence about message
// privacy. Kevin: "simplify this copy." Every line of it was either a fact a control now carries
// — the tier is the locked switch's reason, the addresses are the roster — or a statement about a
// different screen. A card of read-only restatements is how a tab gets long without getting more
// useful.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import FeatureSwitch from '../shared/FeatureSwitch.jsx';
import ConfirmDeleteDialog from '../shared/ConfirmDeleteDialog.jsx';

const stripPlus = (e164) => String(e164 || '').replace(/^\+/, '');

export default function LineMessaging({ line, canWrite }) {
  const qc = useQueryClient();
  const [result, setResult] = useState(null);
  const [confirmOff, setConfirmOff] = useState(false);

  const smsQ = useQuery({
    queryKey: ['subscriber', String(line.subscriberId), 'sms-summary'],
    queryFn: () => api.get(`/subscribers/${line.subscriberId}/sms/summary`),
    enabled: Boolean(line.subscriberId),
    retry: 1,
  });

  // The number's own carrier state. Read from the DID rather than from the line, because
  // it is the DID the carrier holds the feature against.
  const didQ = useQuery({
    queryKey: ['did', stripPlus(line.number)],
    queryFn: () => api.get(`/dids/${encodeURIComponent(stripPlus(line.number))}`),
    enabled: Boolean(line.number),
  });

  // The account's people. Same query key as the voicemail roster and the Users tab, so the list
  // is shared rather than fetched three times.
  const usersQ = useQuery({
    queryKey: ['subscriber', String(line.subscriberId), 'users'],
    queryFn: () => api.get(`/subscribers/${line.subscriberId}/users`),
    enabled: Boolean(line.subscriberId),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['did', stripPlus(line.number)] });
    qc.invalidateQueries({ queryKey: ['subscriber', String(line.subscriberId), 'sms-summary'] });
    qc.invalidateQueries({ queryKey: ['line', String(line.id)] });
  };

  const say = (ok, message) => {
    setResult({ ok, message });
    setTimeout(() => setResult(null), 12_000);
  };

  const enable = useMutation({
    mutationFn: () => api.post(`/dids/${encodeURIComponent(stripPlus(line.number))}/sms/enable`),
    onSuccess: (res) => { say(true, res?.note || 'Text messaging enabled at the carrier.'); invalidate(); },
    onError: (err) => say(false, err?.body?.error || err?.message || 'Enable failed'),
  });
  const disable = useMutation({
    mutationFn: () => api.post(`/dids/${encodeURIComponent(stripPlus(line.number))}/sms/disable`),
    onSuccess: (res) => { setConfirmOff(false); say(true, res?.note || 'Text messaging disabled.'); invalidate(); },
    onError: (err) => { setConfirmOff(false); say(false, err?.body?.error || err?.message || 'Disable failed'); },
  });

  // WHETHER ANYTHING IS EMAILED, on this number. Verso-only — the carrier keeps forwarding to us
  // either way, and what we do with what arrives is ours to decide. So no confirm dialog and no
  // build to wait for: it takes effect on the next text.
  const [pendingField, setPendingField] = useState(null);
  const setEmail = useMutation({
    mutationFn: (next) => api.put(
      `/dids/${encodeURIComponent(stripPlus(line.number))}/sms/email`,
      { email_enabled: next },
    ),
    onSettled: () => setPendingField(null),
    onSuccess: () => invalidate(),
    onError: (err) => say(false, err?.body?.error || err?.message || 'Could not change emailing'),
  });

  // WHO RECEIVES IT. Per person on the account, which is where SMS email already lived — the
  // slider replaces a checkbox on a different page (`sms_notify`, migration 0055).
  const setNotify = useMutation({
    mutationFn: ({ userId, next }) => api.patch(
      `/subscribers/${line.subscriberId}/users/${userId}`,
      { sms_notify: next },
    ),
    onSettled: () => setPendingField(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscriber', String(line.subscriberId), 'users'] }),
    onError: (err) => say(false, err?.body?.error || err?.message || 'Could not change who is emailed'),
  });

  if (!line.number) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-gray-900">Text messaging</h2>
        <p className="mt-2 text-sm text-gray-600">
          This line has no number yet. Messaging is a carrier feature of the number, so there
          is nothing to switch on until one is attached.
        </p>
      </div>
    );
  }

  const sms = smsQ.data || {};
  const tierOn = sms.sms_enabled === true;
  // THE LINE'S GATE, NOT THE CARRIER'S FEATURE FLAG.
  //
  // This read `didQ.data?.features?.sms`, which comes from the DID *list* row the Commio importer
  // stores — and that row carries no messaging fields at all, so it is `false` for every number in
  // the fleet whatever the carrier says. Bryn Long's +17207908069 rendered "Off at the carrier. Texts
  // to this number are not delivered anywhere" over a number Commio had enabled with messaging
  // profile 1330 (2026-08-19).
  //
  // `smsEnabled` (dids.sms_enabled, 0060) is Verso's own decision and the thing that actually
  // decides whether a text reaches anybody — `processInboundSms` reads the same column, so the screen
  // and the pipeline cannot disagree. 1 is on; 0 and null are both off, because nobody having
  // switched it on is not the same as somebody switching it on.
  const on = didQ.data?.smsEnabled === 1;
  // OFF AND GREYED, WITH THE REASON, when something upstream forbids it. Kevin, 2026-08-19: "if
  // the feature is disabled because of the service package, it should be off and grayed out so
  // that it cannot be changed."
  //
  // Two upstream reasons, and they are different answers to the operator:
  //   the PACKAGE does not include messaging — a sales question, change the tier;
  //   the ACCOUNT is not enabled for messaging (0061) — a platform question, we are not running
  //   the feature for anybody yet, and turning it on at the carrier would change nothing a
  //   customer can see because Verso suppresses every inbound.
  const accountOn = didQ.data?.smsFeatureEnabled === 1 || didQ.data?.sms_feature_enabled === 1;
  const users = usersQ.data?.users || [];
  // THIS NUMBER's answer, or the account's while the number has none of its own (0062). The switch
  // shows the effective one and writes an explicit one, which is what touching it means.
  const emailOn = didQ.data?.smsEmailEnabled == null
    ? didQ.data?.subscriberSmsEmailEnabled === 1
    : didQ.data.smsEmailEnabled === 1;
  const locked = !tierOn || !accountOn;
  const lockedReason = !tierOn
    ? 'This customer’s plan does not include text messaging. Change the service tier first.'
    : !accountOn
      ? 'Text messaging is not enabled for this account yet. It is off by default while the '
        + 'feature is being finished, and switched on per account.'
      : null;

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              Text messaging on {line.number}
            </h2>
            {/* WHAT OFF ACTUALLY MEANS. It used to say "off at the carrier", which was both wrong
                about where the switch acts and wrong about this number — Commio keeps forwarding, and
                that is deliberate: the carrier setup is one-time, and this switch is Verso's black
                hole (Kevin, 2026-08-19). "Not delivered anywhere" is the true part and it stays. */}
            <p className="mt-1 text-sm text-gray-600">
              {didQ.isLoading ? 'Checking…' : on
                ? 'On. Texts to this number are delivered and appear in the subscriber’s portal.'
                : 'Off. Texts to this number are received and discarded — nobody is notified.'}
            </p>
          </div>
          {/* THE SAME CONTROL AS THE VOICEMAIL TAB. Kevin, 2026-08-19: "same goes for messaging.
              the two tabs should look and feel the same." It was a badge plus a pair of buttons
              further down the card — two places to look for one answer, and a different shape
              from the tab beside it. Both now import one component, so they cannot drift. */}
          {!didQ.isLoading && (
            <FeatureSwitch
              state={locked ? 'locked' : on ? 'on' : 'off'}
              label="Text messaging for this line"
              canWrite={canWrite}
              busy={enable.isPending || disable.isPending}
              reason={lockedReason}
              onChange={(next) => (next ? enable.mutate() : disable.mutate())}
              // Off at the carrier stops delivery; Verso keeps the thread history either way, so
              // the warning says what is actually lost rather than implying the messages are.
              confirm={{
                title: 'Turn text messaging off for this number?',
                confirmLabel: 'Turn messaging off',
                body: (
                  <>
                    <p>
                      Texts sent to <span className="font-mono">{line.number}</span> will be received
                      and discarded. Nobody is notified, and senders are not told.
                    </p>
                    <p>
                      The conversation history already in Verso is kept, and this takes effect on the
                      next text — nothing is sent to the carrier, so there is no propagation wait and
                      switching it back on is immediate.
                    </p>
                  </>
                ),
              }}
            />
          )}
        </div>

        {result && (
          <div className={`mt-3 rounded-md border px-3 py-2 text-sm ${
            result.ok
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}>
            {result.message}
          </div>
        )}

        {/* The buttons that used to live here are gone: the switch above is the control, and a
            second way to change one thing is how two surfaces end up disagreeing. The note stays,
            because "this one is not held until the next build" is genuinely surprising — and it is
            now true for a better reason: switching off is a Verso-side gate, so it applies to the
            next text rather than waiting on Commio's ~15 minute propagation. */}
        {canWrite && !didQ.isLoading && !locked && (
          <p className="mt-3 text-xs text-gray-500">
            Takes effect on the next text — it is not held until the next build.
          </p>
        )}

        {/* WHETHER TEXTS ARE EMAILED, AND TO WHOM — the same two controls the Voicemail tab has,
            in the same order and the same shapes (Kevin, 2026-08-19), and in the SAME CARD as the
            feature switch, which is how the voicemail tab holds its three. A second bordered card
            with no heading of its own read as an orphan beside it.

            Dimmed while nothing is arriving: with messaging off at the carrier there is nothing to
            email, and a live-looking control over a dead channel is the shape this codebase keeps
            having to unpick. */}
        <div className={`mt-4 max-w-lg space-y-3 border-t border-gray-100 pt-4 ${on ? '' : 'pointer-events-none opacity-40'}`}>
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-gray-700">
              Email text messages
              {/* What still happens when it is off — the same clause the voicemail master carries,
                  for the same reason: without it "off" reads as "the message is lost". */}
              <span className="block text-xs text-gray-400">
                Off = texts still arrive in the customer&rsquo;s portal, just not by email.
              </span>
            </span>
            <FeatureSwitch
              state={emailOn ? 'on' : 'off'}
              label="Email text messages for this line"
              canWrite={canWrite}
              busy={setEmail.isPending && pendingField === 'email'}
              onChange={(next) => { setPendingField('email'); setEmail.mutate(next); }}
            />
          </div>

          <div className={emailOn ? '' : 'pointer-events-none opacity-40'}>
            <div className="text-sm font-medium text-gray-700">Text message email goes to</div>
            {usersQ.isLoading && <p className="mt-1 text-xs text-gray-400">Loading the account&rsquo;s people…</p>}

            {!usersQ.isLoading && users.length === 0 && (
              <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Nobody is on this account yet. Add someone under Account users first — only people on
                the account can receive a customer&rsquo;s texts.
              </p>
            )}

            {users.length > 0 && (
              <ul className="mt-2 divide-y divide-gray-100 rounded-md border border-gray-200 bg-white">
                {users.map((u) => {
                  const off = u.status !== 'active';
                  return (
                    <li key={u.id} className={`flex items-center justify-between gap-3 px-3 py-2 ${off ? 'bg-gray-50' : ''}`}>
                      <span className="min-w-0 text-xs">
                        <span className={`block truncate ${off ? 'text-gray-400' : 'text-gray-900'}`}>
                          {u.name || u.email}
                          {u.isPrimary && <span className="ml-1.5 text-gray-500">main contact</span>}
                        </span>
                        {u.name && <span className={`block truncate ${off ? 'text-gray-400' : 'text-gray-500'}`}>{u.email}</span>}
                      </span>
                      <FeatureSwitch
                        state={off ? 'locked' : (u.smsNotify ? 'on' : 'off')}
                        label={`Text message email to ${u.name || u.email}`}
                        canWrite={canWrite}
                        showState={false}
                        busy={setNotify.isPending && pendingField === `notify-${u.id}`}
                        reason={off ? 'Switched off on the account, so nothing is sent to them.' : null}
                        onChange={(next) => {
                          setPendingField(`notify-${u.id}`);
                          setNotify.mutate({ userId: u.id, next });
                        }}
                      />
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Nobody receiving is a legitimate choice and a dangerous one, and the only thing here
                an operator would otherwise have to work out for themselves. */}
            {users.length > 0 && users.every((u) => !u.smsNotify || u.status !== 'active') && (
              <p className="mt-2 text-xs text-red-700">
                Nobody is emailed. Texts still arrive and are readable in the customer&rsquo;s portal.
              </p>
            )}
          </div>
        </div>
      </div>

      {confirmOff && (
        <ConfirmDeleteDialog
          // NOT a delete, so no typed confirmation: the reversible ones do not get the
          // ceremony that makes the irreversible ones mean something.
          destructive={false}
          title="Disable text messaging"
          body={`Turn SMS off on ${line.number} at Commio — messaging is disabled and its `
            + 'inbound + delivery routing is unassigned. Existing conversations are kept, and '
            + 'it can be switched back on. Carrier propagation can take up to ~15 minutes.'}
          confirmLabel="Disable messaging"
          isPending={disable.isPending}
          error={disable.isError ? (disable.error?.body?.error || disable.error?.message || 'Disable failed') : null}
          onConfirm={() => disable.mutate()}
          onCancel={() => setConfirmOff(false)}
        />
      )}
    </div>
  );
}
